import { API_BASE_URL } from '@synergy/config';
import { Box } from '@mui/material';
import { CoContractorIcon, StarIcon } from '@synergy/theme';
import { ReactNode } from 'react';
import { UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { padZeroLeft } from '@synergy/utils';

import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../../../common/components/autocomplete-list/pool-autocomplete-list';
import { PoolFormAutocompleteList } from '../../../../common/components/abstract/form-autocomplete-list';
import { Supplier } from '../../../../common/models/supplier';
import { SupplierApi } from '../../../../common/api/supplier-api';
import { User } from '../../../../common/models/user';

export interface PartialSupplier extends BaseAutocompleteSuggestionType {
    icons: ReactNode;
    name: string;
    city: string;
}

interface PoolSupplierAutocompleteListProps {
    user: User;
    inProgress: boolean;

    /**
     * Filter suppliers by specifying a customer for the blacklist/whitelist filter.
     */
    isCustomer: boolean;
    accountId: string;
}

class PoolSupplierAutocompleteListBase extends PoolFormAutocompleteList<
    Supplier,
    PartialSupplier,
    PoolSupplierAutocompleteListProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales];
    private api = new SupplierApi(API_BASE_URL);

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public getSuggestionValue = (suggestion: PartialSupplier) => suggestion && suggestion.name;

    public async handleValueChanged(search: string) {
        const searchParams = new URLSearchParams({ search, limit: '40' });

        if (this.props.accountId) {
            const response = this.props.isCustomer
                ? await this.api.cgetCustomerExternalCustomerMapping(searchParams, this.props.accountId)
                : await this.api.cgetSupplierExternalCustomerMapping(searchParams, this.props.accountId);

            const items = response.success === true ? response.result.items : [];
            this.setState({ items });
        }
    }

    public getPartial = (suppliers: Supplier[]): PartialSupplier[] =>
        suppliers.map(supplier => {
            return {
                id: padZeroLeft(supplier.id, 5),
                icons: (
                    <Box sx={{ display: 'inline-flex', verticalAlign: 'middle', gap: 1 }}>
                        {supplier.isMain && <StarIcon sx={{ fontSize: 20 }} />}
                        {supplier.isContractPartner && <CoContractorIcon sx={{ fontSize: 20 }} />}
                    </Box>
                ),
                name: supplier.name,
                city: supplier.getCity() || '',
                tags: supplier.getTags(30),
            };
        });

    public getDisplayValue(supplier: Supplier) {
        const city = (
            supplier.mainAddress ||
            supplier.addresses?.find(address => address.isMainAddress) ||
            supplier.addresses?.[0]
        )?.city;
        return city ? `${supplier.name}, ${city}` : supplier.name;
    }
}

const mapStateToProps = (state: AppState) => ({
    inProgress: state.suppliers.requesting,
    user: state.user,
});

export const PoolExternalCustomerMappingSupplierAutocompleteList = connect(
    mapStateToProps,
    {}
)(PoolSupplierAutocompleteListBase);
